import React, { useState, useEffect, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faTrash,
  faToggleOn,
  faToggleOff,
  faSliders,
  faTimes,
  faFilter,
  faChevronDown,
  faLayerGroup,
} from "@fortawesome/free-solid-svg-icons";
import { getRequest, postRequest, patchRequest, deleteRequest } from "services/api/apiService";

const PLATFORMS = [
  { value: "all", label: "All Platforms" },
  { value: "meta", label: "Meta" },
  { value: "tiktok", label: "TikTok" },
  { value: "google", label: "Google" },
  { value: "snapchat", label: "Snapchat" },
  { value: "linkedin", label: "LinkedIn" },
];

const METRICS = [
  { value: "cpc", label: "CPC (Cost Per Click)" },
  { value: "cpm", label: "CPM (Cost Per 1000 Impressions)" },
  { value: "ctr", label: "CTR (Click-Through Rate %)" },
  { value: "spend", label: "Spend (€)" },
  { value: "roas", label: "ROAS (Return on Ad Spend)" },
];

const OPERATORS = [
  { value: "gt", label: ">" },
  { value: "gte", label: ">=" },
  { value: "lt", label: "<" },
  { value: "lte", label: "<=" },
];

const ACTIONS = [
  { value: "pause", label: "Pause Ad" },
  { value: "enable", label: "Enable Ad" },
];

const PLATFORM_COLORS = {
  all: "bg-gray-100 text-gray-700",
  meta: "bg-blue-100 text-blue-700",
  tiktok: "bg-pink-100 text-pink-700",
  google: "bg-yellow-100 text-yellow-700",
  snapchat: "bg-yellow-50 text-yellow-600",
  linkedin: "bg-sky-100 text-sky-700",
};

const metricLabel = (value) => METRICS.find((m) => m.value === value)?.label?.split(" ")[0] || value.toUpperCase();
const operatorLabel = (value) => OPERATORS.find((o) => o.value === value)?.label || value;
const actionLabel = (value) => ACTIONS.find((a) => a.value === value)?.label || value;
const platformLabel = (value) => PLATFORMS.find((p) => p.value === value)?.label || value;

// ── Platform-specific API helpers ──
const ADSET_LABEL = { meta: "Ad Set", tiktok: "Ad Group", snapchat: "Ad Squad", google: "Ad Group", linkedin: "Ad Group" };

const campaignApiPath = (platform) => {
  if (platform === "meta") return "/api/ads/meta/campaigns";
  if (platform === "tiktok") return "/api/ads/tiktok/campaigns";
  if (platform === "snapchat") return "/api/ads/snapchat/campaigns";
  if (platform === "google") return "/api/ads/google/campaigns";
  return null;
};

const adsetApiPath = (platform, campaignId) => {
  if (platform === "meta") return `/api/ads/meta/adsets?campaign_id=${campaignId}`;
  if (platform === "tiktok") return `/api/ads/tiktok/adgroups?campaign_id=${campaignId}`;
  if (platform === "snapchat") return `/api/ads/snapchat/adsquads?campaign_id=${campaignId}`;
  return null;
};

const adApiPath = (platform, adsetId) => {
  if (platform === "meta") return `/api/ads/meta/ads?adset_id=${adsetId}`;
  if (platform === "tiktok") return `/api/ads/tiktok/ads?adgroup_id=${adsetId}`;
  if (platform === "snapchat") return `/api/ads/snapchat/ads?adsquad_id=${adsetId}`;
  return null;
};

// Normalise varying API shapes → { id, name }
const normalizeCampaigns = (platform, raw) => {
  const data = raw?.data || raw || [];
  return data.map((c) => ({ id: String(c.id || c.campaign_id || ""), name: c.name || c.campaign_name || c.id }));
};

const normalizeAdsets = (platform, raw) => {
  const data = raw?.data || raw || [];
  return data.map((a) => ({
    id: String(a.id || a.adset_id || a.adgroup_id || a.ad_squad_id || ""),
    name: a.name || a.adset_name || a.adgroup_name || a.ad_squad_name || a.id,
  }));
};

const normalizeAds = (platform, raw) => {
  const data = raw?.data || raw || [];
  return data.map((a) => ({
    id: String(a.id || a.ad_id || ""),
    name: a.name || a.ad_name || a.id,
  }));
};

const emptyForm = {
  name: "",
  platform: "all",
  metric: "cpc",
  operator: "gt",
  threshold: "",
  action: "pause",
  ad_account_id: "",
  campaign_id: "",
  campaign_name: "",
  adset_id: "",
  adset_name: "",
  ad_id: "",
  ad_name: "",
};

const AdsRuleSet = () => {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Filters
  const [filterPlatform, setFilterPlatform] = useState("all");
  const [filterAccountId, setFilterAccountId] = useState("");
  const [filterAccounts, setFilterAccounts] = useState([]);

  // Modal ad accounts (for create form)
  const [modalAccounts, setModalAccounts] = useState([]);

  // Cascading scope state
  const [campaigns, setCampaigns] = useState([]);
  const [adsets, setAdsets] = useState([]);
  const [ads, setAds] = useState([]);
  const [loadingCampaigns, setLoadingCampaigns] = useState(false);
  const [loadingAdsets, setLoadingAdsets] = useState(false);
  const [loadingAds, setLoadingAds] = useState(false);

  // ── Fetch ad accounts for filter bar ──
  useEffect(() => {
    const load = async () => {
      try {
        const q = filterPlatform !== "all" ? `?platform=${filterPlatform}` : "";
        const data = await getRequest(`/api/ad-accounts${q}`);
        setFilterAccounts(data);
      } catch {
        /* silent */
      }
    };
    load();
    setFilterAccountId("");
  }, [filterPlatform]);

  // ── Fetch ad accounts for modal (based on form.platform) ──
  useEffect(() => {
    const load = async () => {
      try {
        const q = form.platform !== "all" ? `?platform=${form.platform}` : "";
        const data = await getRequest(`/api/ad-accounts${q}`);
        setModalAccounts(data);
      } catch {
        setModalAccounts([]);
      }
    };
    if (showModal) load();
  }, [form.platform, showModal]);

  // ── Load campaigns when platform changes in modal ──
  useEffect(() => {
    if (!showModal || form.platform === "all") {
      setCampaigns([]);
      setAdsets([]);
      setAds([]);
      return;
    }
    const path = campaignApiPath(form.platform);
    if (!path) return;
    setLoadingCampaigns(true);
    setCampaigns([]);
    setAdsets([]);
    setAds([]);
    getRequest(path)
      .then((raw) => setCampaigns(normalizeCampaigns(form.platform, raw)))
      .catch(() => setCampaigns([]))
      .finally(() => setLoadingCampaigns(false));
  }, [form.platform, showModal]);

  // ── Load ad sets when campaign changes ──
  useEffect(() => {
    if (!form.campaign_id) {
      setAdsets([]);
      setAds([]);
      return;
    }
    const path = adsetApiPath(form.platform, form.campaign_id);
    if (!path) return;
    setLoadingAdsets(true);
    setAdsets([]);
    setAds([]);
    getRequest(path)
      .then((raw) => setAdsets(normalizeAdsets(form.platform, raw)))
      .catch(() => setAdsets([]))
      .finally(() => setLoadingAdsets(false));
  }, [form.campaign_id]);

  // ── Load ads when ad set changes ──
  useEffect(() => {
    if (!form.adset_id) {
      setAds([]);
      return;
    }
    const path = adApiPath(form.platform, form.adset_id);
    if (!path) return;
    setLoadingAds(true);
    setAds([]);
    getRequest(path)
      .then((raw) => setAds(normalizeAds(form.platform, raw)))
      .catch(() => setAds([]))
      .finally(() => setLoadingAds(false));
  }, [form.adset_id]);

  // ── Fetch rules (with filters) ──
  const fetchRules = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterPlatform !== "all") params.set("platform", filterPlatform);
      if (filterAccountId) params.set("ad_account_id", filterAccountId);
      const qs = params.toString();
      const data = await getRequest(`/api/ads-rules${qs ? `?${qs}` : ""}`);
      setRules(data);
    } catch {
      setError("Failed to load rules.");
    } finally {
      setLoading(false);
    }
  }, [filterPlatform, filterAccountId]);

  useEffect(() => {
    fetchRules();
  }, [fetchRules]);

  const openModal = () => {
    setForm(emptyForm);
    setError("");
    setCampaigns([]);
    setAdsets([]);
    setAds([]);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setError("");
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => {
      const updated = { ...prev, [name]: value };
      // Reset downstream when parent changes
      if (name === "platform") {
        updated.ad_account_id = "";
        updated.campaign_id = "";
        updated.campaign_name = "";
        updated.adset_id = "";
        updated.adset_name = "";
        updated.ad_id = "";
        updated.ad_name = "";
      }
      if (name === "campaign_id") {
        const found = campaigns.find((c) => c.id === value);
        updated.campaign_name = found?.name || "";
        updated.adset_id = "";
        updated.adset_name = "";
        updated.ad_id = "";
        updated.ad_name = "";
      }
      if (name === "adset_id") {
        const found = adsets.find((a) => a.id === value);
        updated.adset_name = found?.name || "";
        updated.ad_id = "";
        updated.ad_name = "";
      }
      if (name === "ad_id") {
        const found = ads.find((a) => a.id === value);
        updated.ad_name = found?.name || "";
      }
      return updated;
    });
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || form.threshold === "") {
      setError("Name and threshold are required.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const payload = { ...form };
      if (!payload.ad_account_id) delete payload.ad_account_id;
      if (!payload.campaign_id) { delete payload.campaign_id; delete payload.campaign_name; }
      if (!payload.adset_id) { delete payload.adset_id; delete payload.adset_name; }
      if (!payload.ad_id) { delete payload.ad_id; delete payload.ad_name; }
      const created = await postRequest("/api/ads-rules", payload);
      setRules((prev) => [created, ...prev]);
      closeModal();
    } catch {
      setError("Failed to create rule.");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (rule) => {
    try {
      const updated = await patchRequest(`/api/ads-rules/${rule.id}`, {
        is_active: !rule.is_active,
      });
      setRules((prev) => prev.map((r) => (r.id === rule.id ? updated : r)));
    } catch {
      // silent
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this rule?")) return;
    try {
      await deleteRequest(`/api/ads-rules/${id}`);
      setRules((prev) => prev.filter((r) => r.id !== id));
    } catch {
      alert("Failed to delete rule.");
    }
  };

  const adsetLabel = ADSET_LABEL[form.platform] || "Ad Set";

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <FontAwesomeIcon icon={faSliders} className="text-[#221C51] text-xl" />
          <div>
            <h1 className="text-xl font-bold text-[#221C51]">Ads RuleSet</h1>
            <p className="text-xs text-gray-500">
              Automatic rules that protect your budget — ads are paused or enabled when conditions are met.
            </p>
          </div>
        </div>
        <button
          onClick={openModal}
          className="flex items-center bg-[#221C51] text-white px-4 py-2 rounded-lg text-sm hover:bg-[#2e265e] transition"
        >
          <FontAwesomeIcon icon={faPlus} className="mr-2" />
          New Rule
        </button>
      </div>

      {/* ── Filter Bar ── */}
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="flex items-center space-x-2 text-xs text-gray-500">
          <FontAwesomeIcon icon={faFilter} />
          <span className="font-semibold">Filters</span>
        </div>

        <div className="relative">
          <select
            value={filterPlatform}
            onChange={(e) => setFilterPlatform(e.target.value)}
            className="appearance-none border border-gray-200 rounded-lg pl-3 pr-8 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#221C51]/30"
          >
            {PLATFORMS.map((p) => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>
          <FontAwesomeIcon icon={faChevronDown} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-[10px] pointer-events-none" />
        </div>

        <div className="relative">
          <select
            value={filterAccountId}
            onChange={(e) => setFilterAccountId(e.target.value)}
            className="appearance-none border border-gray-200 rounded-lg pl-3 pr-8 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#221C51]/30"
          >
            <option value="">All Accounts</option>
            {filterAccounts.map((a) => (
              <option key={a.id} value={a.id}>{a.account_name}</option>
            ))}
          </select>
          <FontAwesomeIcon icon={faChevronDown} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-[10px] pointer-events-none" />
        </div>
      </div>

      {/* Body */}
      {loading ? (
        <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">Loading rules…</div>
      ) : rules.length === 0 ? (
        <EmptyState onNew={openModal} />
      ) : (
        <div className="space-y-3 overflow-y-auto">
          {rules.map((rule) => (
            <RuleCard key={rule.id} rule={rule} onToggle={toggleActive} onDelete={handleDelete} />
          ))}
        </div>
      )}

      {/* ── Create Modal ── */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b sticky top-0 bg-white z-10">
              <h2 className="font-bold text-[#221C51] text-lg">New Automation Rule</h2>
              <button onClick={closeModal} className="text-gray-400 hover:text-gray-600">
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <form onSubmit={handleCreate} className="px-6 py-5 space-y-4">
              {error && (
                <p className="text-red-500 text-xs bg-red-50 rounded px-3 py-2">{error}</p>
              )}

              {/* Rule Name */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Rule Name</label>
                <input
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleFormChange}
                  placeholder="e.g. Stop high CPC ads"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#221C51]/30"
                />
              </div>

              {/* Platform */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Platform</label>
                <div className="relative">
                  <select
                    name="platform"
                    value={form.platform}
                    onChange={handleFormChange}
                    className="w-full appearance-none border border-gray-200 rounded-lg px-3 pr-8 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#221C51]/30"
                  >
                    {PLATFORMS.map((p) => (
                      <option key={p.value} value={p.value}>{p.label}</option>
                    ))}
                  </select>
                  <FontAwesomeIcon icon={faChevronDown} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-[10px] pointer-events-none" />
                </div>
              </div>

              {/* Ad Account */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">
                  Ad Account <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <div className="relative">
                  <select
                    name="ad_account_id"
                    value={form.ad_account_id}
                    onChange={handleFormChange}
                    className="w-full appearance-none border border-gray-200 rounded-lg px-3 pr-8 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#221C51]/30"
                  >
                    <option value="">All accounts</option>
                    {modalAccounts.map((a) => (
                      <option key={a.id} value={a.id}>{a.account_name}</option>
                    ))}
                  </select>
                  <FontAwesomeIcon icon={faChevronDown} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-[10px] pointer-events-none" />
                </div>
              </div>

              {/* ── Scope section ── */}
              {form.platform !== "all" && (
                <div className="border border-dashed border-gray-200 rounded-xl p-4 space-y-3 bg-gray-50/60">
                  <div className="flex items-center gap-2 text-xs font-bold text-[#221C51] uppercase tracking-wide">
                    <FontAwesomeIcon icon={faLayerGroup} className="text-[10px]" />
                    Target Scope <span className="font-normal text-gray-400 normal-case tracking-normal">(optional — narrows which objects the rule watches)</span>
                  </div>

                  {/* Campaign */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Campaign</label>
                    <div className="relative">
                      <select
                        name="campaign_id"
                        value={form.campaign_id}
                        onChange={handleFormChange}
                        disabled={loadingCampaigns || campaigns.length === 0}
                        className="w-full appearance-none border border-gray-200 rounded-lg px-3 pr-8 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#221C51]/30 disabled:opacity-50"
                      >
                        <option value="">
                          {loadingCampaigns ? "Loading…" : campaigns.length === 0 ? "No campaigns found" : "All campaigns"}
                        </option>
                        {campaigns.map((c) => (
                          <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                      </select>
                      <FontAwesomeIcon icon={faChevronDown} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-[10px] pointer-events-none" />
                    </div>
                  </div>

                  {/* Ad Set / Ad Group */}
                  {form.campaign_id && (
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">{adsetLabel}</label>
                      <div className="relative">
                        <select
                          name="adset_id"
                          value={form.adset_id}
                          onChange={handleFormChange}
                          disabled={loadingAdsets}
                          className="w-full appearance-none border border-gray-200 rounded-lg px-3 pr-8 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#221C51]/30 disabled:opacity-50"
                        >
                          <option value="">
                            {loadingAdsets ? "Loading…" : adsets.length === 0 ? `No ${adsetLabel.toLowerCase()}s found` : `All ${adsetLabel.toLowerCase()}s`}
                          </option>
                          {adsets.map((a) => (
                            <option key={a.id} value={a.id}>{a.name}</option>
                          ))}
                        </select>
                        <FontAwesomeIcon icon={faChevronDown} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-[10px] pointer-events-none" />
                      </div>
                    </div>
                  )}

                  {/* Ad */}
                  {form.adset_id && (
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Specific Ad</label>
                      <div className="relative">
                        <select
                          name="ad_id"
                          value={form.ad_id}
                          onChange={handleFormChange}
                          disabled={loadingAds}
                          className="w-full appearance-none border border-gray-200 rounded-lg px-3 pr-8 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#221C51]/30 disabled:opacity-50"
                        >
                          <option value="">
                            {loadingAds ? "Loading…" : ads.length === 0 ? "No ads found" : "All ads in this group"}
                          </option>
                          {ads.map((a) => (
                            <option key={a.id} value={a.id}>{a.name}</option>
                          ))}
                        </select>
                        <FontAwesomeIcon icon={faChevronDown} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-[10px] pointer-events-none" />
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Condition row */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Metric</label>
                  <select
                    name="metric"
                    value={form.metric}
                    onChange={handleFormChange}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#221C51]/30"
                  >
                    {METRICS.map((m) => (
                      <option key={m.value} value={m.value}>{m.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Operator</label>
                  <select
                    name="operator"
                    value={form.operator}
                    onChange={handleFormChange}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#221C51]/30"
                  >
                    {OPERATORS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Value (€)</label>
                  <input
                    type="number"
                    name="threshold"
                    value={form.threshold}
                    onChange={handleFormChange}
                    placeholder="10"
                    min="0"
                    step="0.01"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#221C51]/30"
                  />
                </div>
              </div>

              {/* Action */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Action</label>
                <select
                  name="action"
                  value={form.action}
                  onChange={handleFormChange}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#221C51]/30"
                >
                  {ACTIONS.map((a) => (
                    <option key={a.value} value={a.value}>{a.label}</option>
                  ))}
                </select>
              </div>

              {/* Preview */}
              <div className="bg-[#221C51]/5 rounded-lg px-4 py-3 text-sm text-[#221C51]">
                <span className="font-semibold">Preview: </span>
                If <span className="font-bold">{metricLabel(form.metric)}</span>{" "}
                <span className="font-bold">{operatorLabel(form.operator)}</span>{" "}
                <span className="font-bold">{form.threshold || "?"} €</span> on{" "}
                <span className="font-bold">{platformLabel(form.platform)}</span>
                {form.campaign_name && (
                  <span className="text-xs text-gray-600"> › <span className="font-medium">{form.campaign_name}</span></span>
                )}
                {form.adset_name && (
                  <span className="text-xs text-gray-600"> › <span className="font-medium">{form.adset_name}</span></span>
                )}
                {form.ad_name && (
                  <span className="text-xs text-gray-600"> › <span className="font-medium">{form.ad_name}</span></span>
                )}
                {" → "}
                <span className="font-bold text-red-600">{actionLabel(form.action)}</span>
              </div>

              <div className="flex justify-end space-x-3 pt-1">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 text-sm rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-5 py-2 text-sm rounded-lg bg-[#221C51] text-white hover:bg-[#2e265e] transition disabled:opacity-60"
                >
                  {saving ? "Saving…" : "Create Rule"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const RuleCard = ({ rule, onToggle, onDelete }) => (
  <div
    className={`flex items-start justify-between border rounded-xl px-5 py-4 transition ${
      rule.is_active ? "border-[#221C51]/20 bg-white" : "border-gray-200 bg-gray-50 opacity-60"
    }`}
  >
    <div className="flex items-start space-x-4 min-w-0 flex-1">
      <div
        className={`text-xs font-semibold px-2 py-1 rounded-full whitespace-nowrap mt-0.5 ${
          PLATFORM_COLORS[rule.platform] || PLATFORM_COLORS.all
        }`}
      >
        {platformLabel(rule.platform)}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center flex-wrap gap-2">
          <p className="font-semibold text-[#221C51] text-sm">{rule.name}</p>
          {rule.ad_account_name && (
            <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 whitespace-nowrap">
              {rule.ad_account_name}
            </span>
          )}
        </div>

        {/* Scope breadcrumb */}
        {(rule.campaign_name || rule.adset_name || rule.ad_name) && (
          <div className="flex items-center gap-1 mt-1 flex-wrap">
            <FontAwesomeIcon icon={faLayerGroup} className="text-[9px] text-gray-400" />
            {rule.campaign_name && (
              <span className="text-[11px] text-gray-500 font-medium">{rule.campaign_name}</span>
            )}
            {rule.adset_name && (
              <>
                <span className="text-gray-300 text-[10px]">›</span>
                <span className="text-[11px] text-gray-500 font-medium">{rule.adset_name}</span>
              </>
            )}
            {rule.ad_name && (
              <>
                <span className="text-gray-300 text-[10px]">›</span>
                <span className="text-[11px] text-indigo-600 font-semibold">{rule.ad_name}</span>
              </>
            )}
          </div>
        )}

        <p className="text-xs text-gray-500 mt-1">
          If <span className="font-medium">{metricLabel(rule.metric)}</span>{" "}
          {operatorLabel(rule.operator)}{" "}
          <span className="font-medium">{rule.threshold} €</span>{" "}
          → <span className="font-medium text-red-600">{actionLabel(rule.action)}</span>
        </p>
      </div>
    </div>
    <div className="flex items-center space-x-3 ml-4 shrink-0">
      <button
        onClick={() => onToggle(rule)}
        title={rule.is_active ? "Disable rule" : "Enable rule"}
        className="text-2xl transition"
      >
        <FontAwesomeIcon
          icon={rule.is_active ? faToggleOn : faToggleOff}
          className={rule.is_active ? "text-[#221C51]" : "text-gray-400"}
        />
      </button>
      <button
        onClick={() => onDelete(rule.id)}
        title="Delete rule"
        className="text-gray-300 hover:text-red-500 transition"
      >
        <FontAwesomeIcon icon={faTrash} />
      </button>
    </div>
  </div>
);

const EmptyState = ({ onNew }) => (
  <div className="flex-1 flex flex-col items-center justify-center text-center py-20">
    <FontAwesomeIcon icon={faSliders} className="text-5xl text-gray-200 mb-4" />
    <p className="text-gray-500 font-medium mb-1">No rules yet</p>
    <p className="text-gray-400 text-sm mb-6 max-w-xs">
      Create automation rules to automatically pause or enable ads when conditions like high CPC are met.
    </p>
    <button
      onClick={onNew}
      className="flex items-center bg-[#221C51] text-white px-5 py-2 rounded-lg text-sm hover:bg-[#2e265e] transition"
    >
      <FontAwesomeIcon icon={faPlus} className="mr-2" />
      Create my first rule
    </button>
  </div>
);

export default AdsRuleSet;
